import { useState } from 'react';
import { Menu, Bell, ChevronDown, LogOut, User, Shield } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useBranding } from '@/hooks/useBranding';
import { authService } from '@/services/auth.service';
import { toast } from '@/hooks/useToast';
import { USER_ROLES } from '@/types';

interface HeaderProps {
  onMenuToggle?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuToggle, showMenuButton = true }: HeaderProps) {
  const { appUser, firebaseUser } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const [loggingOut, setLoggingOut] = useState(false);

  const prefix = tenantSlug ? `/${tenantSlug}` : '';

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await authService.signOut();
      navigate('/login');
    } catch {
      toast.error('Erro ao sair. Tente novamente.');
    } finally {
      setLoggingOut(false);
    }
  }

  const displayName = appUser?.displayName ?? firebaseUser?.displayName ?? 'Usuário';
  const email = appUser?.email ?? firebaseUser?.email ?? '';
  const roleLabel = appUser?.role ? USER_ROLES[appUser.role] : '';

  return (
    <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-6 shrink-0">
      <div className="flex items-center gap-3">
        {showMenuButton && (
          <button
            onClick={onMenuToggle}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors lg:hidden"
            aria-label="Toggle menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        {/* Company name for mobile */}
        <span className="text-sm font-semibold text-slate-800 lg:hidden">
          {branding.companyName}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Notifications — future feature */}
        <button className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors relative">
          <Bell className="h-4 w-4" />
        </button>

        {/* User menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-100 transition-colors">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-xs font-medium text-slate-900 leading-tight">{displayName}</p>
                <p className="text-[10px] text-slate-500 leading-tight">{roleLabel}</p>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 min-w-[200px] rounded-xl border border-slate-200 bg-white shadow-floating p-1 animate-slide-up"
              align="end"
              sideOffset={8}
            >
              <div className="px-3 py-2 border-b border-slate-100 mb-1">
                <p className="text-xs font-medium text-slate-900 truncate">{displayName}</p>
                <p className="text-[11px] text-slate-500 truncate">{email}</p>
                <div className="mt-1 flex items-center gap-1">
                  <Shield className="h-3 w-3 text-primary-600" />
                  <span className="text-[11px] text-primary-600 font-medium">{roleLabel}</span>
                </div>
              </div>

              <DropdownMenu.Item
                onSelect={() => navigate(`${prefix}/profile`)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700',
                  'cursor-pointer hover:bg-slate-50 hover:text-slate-900 outline-none',
                )}
              >
                <User className="h-4 w-4" />
                Meu perfil
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="my-1 border-t border-slate-100" />

              <DropdownMenu.Item
                onSelect={handleLogout}
                disabled={loggingOut}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600',
                  'cursor-pointer hover:bg-red-50 outline-none disabled:opacity-50',
                )}
              >
                <LogOut className="h-4 w-4" />
                {loggingOut ? 'Saindo...' : 'Sair'}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
